/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources, IProductSearchResult, ProductComponentV2 } from '@msdyn365-commerce/components';
import { IComponent, IComponentProps, ICoreContext, IImageSettings, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISearchResultContainerV2Resources } from '../search-result-container-v2.props.autogenerated';

/**
 * ProductSearchResultItems interface.
 */
interface IProductSearchResultItems {
    products: IProductSearchResult[];
    context: ICoreContext;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    shouldAllowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickViewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
}

/**
 * IProductSearchResultItemsProps Interface .
 */
export interface IProductSearchResultItemsProps extends IComponentProps<IProductSearchResultItems> {}

/**
 * IProductSearchResultItemsComponent Interface .
 */
export interface IProductSearchResultItemsComponent extends IComponent<IProductSearchResultItemsProps> {}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
export function getInventoryLabel(
    channelInventoryConfigurationId: number | undefined,
    product: IProductSearchResult,
    props: IProductSearchResultItemsProps
): string | undefined {
    const enableStockCheck = props.context.app.config.enableStockCheck;
    if (!enableStockCheck || !channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.attributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.attributeValues.find(attribute => attribute.recordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.textValue;
    }
    return undefined;
}

/**
 * Product Search Result Items
 * @param props -Invoice table props.
 * @returns On change.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention -- Component name should start with Capital letter
const ProductSearchResultItems: React.FC<IProductSearchResultItemsProps> = (props: IProductSearchResultItemsProps) => {
    const {
        products,
        context,
        imageSettings,
        moduleType,
        moduleId,
        shouldAllowBackNavigation,
        telemetryContent,
        quickViewSlot,
        productComparisonButton,
        channelInventoryConfigurationId,
        isPriceMinMaxEnabled,
        productsDimensionAvailabilities
    } = props.data;
    const resources = props.moduleResources as ISearchResultContainerV2Resources;
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };
    return (
        <ul className='list-unstyled'>
            {products.map((product: IProductSearchResult, index: number) => (
                <li className='ms-product-search-result__item' key={index}>
                    <ProductComponentV2
                        context={context}
                        telemetryContent={telemetryContent}
                        imageSettings={imageSettings}
                        freePriceText={resources.priceFree}
                        originalPriceText={resources.originalPriceText}
                        currentPriceText={resources.currentPriceText}
                        ratingAriaLabel={resources.ratingAriaLabel}
                        isAllowBack={shouldAllowBackNavigation}
                        id={moduleId}
                        key={product.recordId}
                        typeName={moduleType}
                        data={{ product }}
                        quickViewButton={quickViewSlot}
                        productComparisonButton={productComparisonButton}
                        inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product, props)}
                        isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                        priceResources={priceResources}
                        swatchItemAriaLabel={resources.swatchItemAriaLabel}
                        dimensionAvailabilities={productsDimensionAvailabilities?.find(
                            dimensionAvailability =>
                                ArrayExtensions.hasElements(dimensionAvailability) &&
                                dimensionAvailability[0].masterProductId === product.masterProductId
                        )}
                    />
                </li>
            ))}
        </ul>
    );
};

/**
 * Product Search Result Items component.
 */
export const ProductSearchResultItemsComponent: React.FunctionComponent<IProductSearchResultItemsProps> = msdyn365Commerce.createComponent<
    IProductSearchResultItemsComponent
>('Product-Search-Result-Items', { component: ProductSearchResultItems });
